/**
 * Export compositor — draws the reel's essential broadcast overlays with canvas 2D.
 *
 * DOM graphics aren't captured by canvas.captureStream(), so for "Save video" the
 * reel renders the WebGL program frame, drawImage()s it into a 2D canvas, and then
 * this module paints the scorebug, lower third, verdict stamp, replay bug, stinger
 * and intro/outro cards on top. Everything is authored in 1920×1080 design units
 * (same grid as src/graphics) and scaled to the output size.
 */

export interface CardInfo {
  competition: string;
  venue: string;
  homeName: string; awayName: string;
  homeShort: string; awayShort: string;
  homeColor: string; awayColor: string;
  score: { home: number; away: number };
  letter: string;
  letterColor: string;
  rating: number;
  goalsLine: string;
  clipCount: number;
}

export interface OverlayView {
  scorebug: null | { sh: number; sa: number; clock: string; period: string; extra: string | null };
  lowerThird: null | { minute: string; title: string; text: string; accent: string; age: number; left: number };
  stamp: null | { label: string; sub: string; tone: 'good' | 'bad' | 'info'; age: number };
  replay: null | { slow: boolean; label: string };
  card: null | { kind: 'intro' | 'outro'; age: number };
  /** Seconds since the stinger started (or < 0 when idle). */
  stingerAge: number;
  /** "HIGHLIGHTS" corner tag + progress context. */
  tag: string;
}

const FONT = '"Barlow Condensed", "Arial Narrow", sans-serif';
const BRAND = '#5b3cff', BRAND_2 = '#00c8ff';
const ease = (k: number) => 1 - Math.pow(1 - Math.max(0, Math.min(1, k)), 3);
const clamp01 = (k: number) => Math.max(0, Math.min(1, k));

function inkFor(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#fff';
  const n = parseInt(m[1]!, 16);
  const lin = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255) > 0.42 ? '#0b0e14' : '#ffffff';
}

function brandGrad(g: CanvasRenderingContext2D, x0: number, x1: number): CanvasGradient {
  const gr = g.createLinearGradient(x0, 0, x1, 0);
  gr.addColorStop(0, BRAND); gr.addColorStop(0.55, '#3d6bff'); gr.addColorStop(1, BRAND_2);
  return gr;
}

function glass(g: CanvasRenderingContext2D, y0: number, y1: number): CanvasGradient {
  const gr = g.createLinearGradient(0, y0, 0, y1);
  gr.addColorStop(0, 'rgba(22,26,36,0.94)'); gr.addColorStop(1, 'rgba(9,11,17,0.95)');
  return gr;
}

function text(g: CanvasRenderingContext2D, s: string, x: number, y: number, size: number, opts: { weight?: number; italic?: boolean; color?: string; align?: CanvasTextAlign; spacing?: number; maxW?: number } = {}): number {
  g.font = `${opts.italic ? 'italic ' : ''}${opts.weight ?? 700} ${size}px ${FONT}`;
  g.fillStyle = opts.color ?? '#fff';
  g.textAlign = opts.align ?? 'left';
  g.textBaseline = 'middle';
  const sp = opts.spacing ?? 0;
  if ('letterSpacing' in g) (g as unknown as { letterSpacing: string }).letterSpacing = `${sp * size}px`;
  if (opts.maxW) g.fillText(s, x, y, opts.maxW); else g.fillText(s, x, y);
  const w = g.measureText(s).width;
  if ('letterSpacing' in g) (g as unknown as { letterSpacing: string }).letterSpacing = '0px';
  return opts.maxW ? Math.min(w, opts.maxW) : w;
}

function para(g: CanvasRenderingContext2D, pts: [number, number][], fill: string | CanvasGradient): void {
  g.beginPath();
  g.moveTo(pts[0]![0], pts[0]![1]);
  for (let i = 1; i < pts.length; i++) g.lineTo(pts[i]![0], pts[i]![1]);
  g.closePath();
  g.fillStyle = fill;
  g.fill();
}

// ---------------------------------------------------------------------------

function drawScorebug(g: CanvasRenderingContext2D, c: CardInfo, sb: NonNullable<OverlayView['scorebug']>): void {
  const x = 96, y = 54, h = 52;
  g.save();
  g.shadowColor = 'rgba(0,0,0,0.45)'; g.shadowBlur = 14; g.shadowOffsetY = 6;
  let cx = x + 48;
  const teamW = 128;
  // home
  g.fillStyle = glass(g, y, y + h); g.fillRect(cx, y, teamW, h);
  g.shadowColor = 'transparent';
  // badge (on top of the home panel's left edge)
  para(g, [[x, y], [x + 58, y], [x + 48, y + h], [x, y + h]], brandGrad(g, x, x + 58));
  text(g, 'SD', x + 27, y + h / 2 + 2, 30, { weight: 800, italic: true, align: 'center' });
  g.fillStyle = c.homeColor; g.fillRect(cx + 22, y + 9, 8, h - 18);
  text(g, c.homeShort, cx + 42, y + h / 2 + 2, 31, { weight: 700, spacing: 0.06 });
  cx += teamW;
  // score
  const scW = 104;
  const paper = g.createLinearGradient(0, y, 0, y + h); paper.addColorStop(0, '#fff'); paper.addColorStop(1, '#edf0f5');
  g.fillStyle = paper; g.fillRect(cx, y, scW, h);
  text(g, String(sb.sh), cx + 28, y + h / 2 + 3, 38, { weight: 800, color: '#0b0e14', align: 'center' });
  g.fillStyle = 'rgba(11,14,20,0.45)'; g.fillRect(cx + scW / 2 - 4.5, y + h / 2, 9, 3);
  text(g, String(sb.sa), cx + scW - 28, y + h / 2 + 3, 38, { weight: 800, color: '#0b0e14', align: 'center' });
  cx += scW;
  // away
  g.fillStyle = glass(g, y, y + h); g.fillRect(cx, y, teamW, h);
  text(g, c.awayShort, cx + teamW - 42, y + h / 2 + 2, 31, { weight: 700, spacing: 0.06, align: 'right' });
  g.fillStyle = c.awayColor; g.fillRect(cx + teamW - 30, y + 9, 8, h - 18);
  cx += teamW;
  // clock
  const clW = 150;
  const g2 = g.createLinearGradient(0, y, 0, y + h); g2.addColorStop(0, 'rgba(34,40,54,0.95)'); g2.addColorStop(1, 'rgba(18,21,30,0.96)');
  para(g, [[cx, y], [cx + clW, y], [cx + clW - 10, y + h], [cx, y + h]], g2);
  text(g, sb.clock, cx + 18, y + h / 2 + 2, 31, { weight: 600 });
  text(g, sb.period, cx + 102, y + h / 2 + 2, 15, { weight: 700, color: BRAND_2, spacing: 0.14 });
  cx += clW;
  if (sb.extra) {
    para(g, [[cx - 6 + 10, y + 9], [cx + 74, y + 9], [cx + 64, y + h - 9], [cx - 6, y + h - 9]], brandGrad(g, cx, cx + 74));
    text(g, sb.extra, cx + 34, y + h / 2 + 1, 24, { weight: 700, align: 'center' });
  }
  g.restore();
}

function drawLowerThird(g: CanvasRenderingContext2D, lt: NonNullable<OverlayView['lowerThird']>): void {
  const kin = ease(lt.age / 0.45), kout = lt.left < 0.4 ? clamp01(lt.left / 0.4) : 1;
  const x = 150, y = 860, h = 112;
  g.save();
  g.globalAlpha = kout;
  g.font = `600 30px ${FONT}`;
  const bodyW = g.measureText(lt.text).width;
  g.font = `italic 800 44px ${FONT}`;
  const titleW = g.measureText(lt.title).width * 1.04 + lt.title.length * 44 * 0.04;
  const tw = Math.max(520, Math.min(1180, Math.max(bodyW, titleW) + 80));
  const minW = 150;
  const reveal = (minW + tw + 40) * kin;
  g.beginPath(); g.rect(x - 20, y - 40, reveal + 20, h + 80); g.clip();
  g.shadowColor = 'rgba(0,0,0,0.45)'; g.shadowBlur = 18; g.shadowOffsetY = 6;
  // minute block
  g.fillStyle = lt.accent; g.fillRect(x, y, minW, h);
  g.shadowColor = 'transparent';
  text(g, lt.minute, x + minW / 2, y + h / 2 + 4, 62, { weight: 800, italic: true, color: inkFor(lt.accent), align: 'center' });
  // title bar
  g.fillStyle = glass(g, y, y + h); g.fillRect(x + minW, y, tw, h);
  g.fillStyle = brandGrad(g, x + minW, x + minW + tw); g.fillRect(x + minW, y + h - 6, tw, 6);
  text(g, lt.title, x + minW + 30, y + 38, 44, { weight: 800, italic: true, spacing: 0.04 });
  text(g, lt.text, x + minW + 30, y + 80, 30, { weight: 600, color: 'rgba(232,238,248,0.78)', maxW: tw - 50 });
  g.restore();
}

function drawStamp(g: CanvasRenderingContext2D, st: NonNullable<OverlayView['stamp']>): void {
  const k = st.age < 0.25 ? st.age / 0.25 : 1;
  const scale = st.age < 0.25 ? 2.2 - 1.2 * ease(k) : 1;
  const col = st.tone === 'good' ? '#19d36b' : st.tone === 'bad' ? '#ff2d2d' : '#ffb020';
  g.save();
  g.translate(1560, 300);
  g.rotate(-0.13);
  g.scale(scale, scale);
  g.globalAlpha = Math.min(1, k * 1.4);
  g.font = `italic 800 92px ${FONT}`;
  const w = g.measureText(st.label).width + 70;
  const h = 120;
  g.fillStyle = 'rgba(8,9,12,0.55)';
  g.fillRect(-w / 2, -h / 2, w, h);
  g.strokeStyle = col; g.lineWidth = 9; g.strokeRect(-w / 2, -h / 2, w, h);
  g.lineWidth = 3; g.strokeRect(-w / 2 + 12, -h / 2 + 12, w - 24, h - 24);
  text(g, st.label, 0, 6, 92, { weight: 800, italic: true, color: col, align: 'center' });
  if (st.sub) {
    g.font = `700 26px ${FONT}`;
    const sw = g.measureText(st.sub).width + 40;
    g.fillStyle = col; g.fillRect(-sw / 2, h / 2 + 10, sw, 40);
    text(g, st.sub, 0, h / 2 + 31, 26, { weight: 700, color: '#0b0e14', align: 'center', spacing: 0.12 });
  }
  g.restore();
}

function drawReplayBug(g: CanvasRenderingContext2D, rb: NonNullable<OverlayView['replay']>, t: number): void {
  const x1 = 1920 - 96, y = 54, h = 56, w = 250;
  g.save();
  g.shadowColor = 'rgba(0,0,0,0.45)'; g.shadowBlur = 14; g.shadowOffsetY = 6;
  para(g, [[x1 - w + 14, y], [x1, y], [x1, y + h], [x1 - w, y + h]], brandGrad(g, x1 - w, x1));
  g.shadowColor = 'transparent';
  if (Math.floor(t * 1.8) % 2 === 0) { g.fillStyle = '#fff'; g.beginPath(); g.arc(x1 - w + 46, y + h / 2, 9, 0, Math.PI * 2); g.fill(); }
  text(g, 'REPLAY', x1 - 30, y + h / 2 + 3, 38, { weight: 800, italic: true, align: 'right', spacing: 0.08 });
  if (rb.slow) {
    g.fillStyle = glass(g, y + h, y + h + 30); g.fillRect(x1 - 190, y + h, 190, 30);
    text(g, rb.label, x1 - 95, y + h + 16, 19, { weight: 700, color: BRAND_2, align: 'center', spacing: 0.16 });
  }
  g.restore();
}

function drawStinger(g: CanvasRenderingContext2D, c: CardInfo, age: number): void {
  if (age < 0 || age > 0.68) return;
  const band = (delay: number, fill: string | CanvasGradient, left: number, width: number) => {
    const k = clamp01((age - delay) / 0.6);
    // keyframes: 0 → -120%, 42..58% → 0, 100% → +120% (cubic-ish ease)
    let tx: number;
    if (k < 0.42) tx = -1.2 * (1 - ease(k / 0.42));
    else if (k < 0.58) tx = 0;
    else tx = 1.2 * Math.pow((k - 0.58) / 0.42, 2);
    const W = 1920 * width, X = 1920 * left + tx * W;
    const sk = Math.tan(18 * Math.PI / 180) * 1300;
    para(g, [[X + sk / 2, -110], [X + W + sk / 2, -110], [X + W - sk / 2, 1190], [X - sk / 2, 1190]], fill);
  };
  g.save();
  band(0, c.homeColor, -0.25, 1.5);
  band(0.03, brandGrad(g, 0, 1920), -0.25, 1.5);
  band(0.07, c.awayColor, -0.06, 0.4);
  const k = clamp01((age - 0.03) / 0.6);
  if (k > 0.25 && k < 0.75) {
    const s = k < 0.45 ? 0.7 + 0.38 * ((k - 0.25) / 0.2) : k < 0.58 ? 1.08 - 0.08 * ((k - 0.45) / 0.13) : 1;
    g.translate(960, 540); g.scale(s, s);
    g.strokeStyle = '#fff'; g.lineWidth = 6; g.strokeRect(-115, -75, 230, 150);
    text(g, 'SD', 0, 6, 150, { weight: 800, italic: true, align: 'center' });
  }
  g.restore();
}

function drawCard(g: CanvasRenderingContext2D, c: CardInfo, card: NonNullable<OverlayView['card']>): void {
  const a = ease(card.age / 0.6);
  g.save();
  // backdrop
  const bg = g.createLinearGradient(0, 0, 0, 1080);
  bg.addColorStop(0, 'rgba(6,7,10,0.78)'); bg.addColorStop(1, 'rgba(6,7,10,0.92)');
  g.globalAlpha = Math.min(1, card.age / 0.35);
  g.fillStyle = bg; g.fillRect(0, 0, 1920, 1080);
  // team colour wedges
  const wedge = (x0: number, dir: 1 | -1, col: string) => {
    const gr = g.createLinearGradient(x0, 0, x0 + dir * 300, 0);
    gr.addColorStop(0.35, col); gr.addColorStop(1, 'rgba(0,0,0,0)');
    g.globalAlpha = 0.9 * Math.min(1, card.age / 0.35);
    para(g, [[x0, 0], [x0 + dir * 300 * a, 0], [x0 + dir * 180 * a, 1080], [x0, 1080]], gr);
  };
  wedge(0, 1, c.homeColor);
  wedge(1920, -1, c.awayColor);
  g.globalAlpha = 1;
  const k = (d: number) => ease((card.age - d) / 0.5);
  if (card.kind === 'intro') {
    g.globalAlpha = k(0.15);
    text(g, 'MATCH HIGHLIGHTS', 960, 250, 40, { weight: 700, color: BRAND_2, align: 'center', spacing: 0.4 });
    text(g, c.competition.toUpperCase(), 960, 310, 34, { weight: 600, color: 'rgba(232,238,248,0.75)', align: 'center', spacing: 0.12 });
    g.globalAlpha = k(0.3);
    text(g, c.homeName.toUpperCase(), 820, 520, 72, { weight: 800, italic: true, align: 'right', maxW: 620 });
    text(g, c.awayName.toUpperCase(), 1100, 520, 72, { weight: 800, italic: true, align: 'left', maxW: 620 });
    const paper = g.createLinearGradient(0, 460, 0, 580); paper.addColorStop(0, '#fff'); paper.addColorStop(1, '#e6e9ef');
    g.fillStyle = paper; g.fillRect(860, 460, 200, 120);
    text(g, `${c.score.home}–${c.score.away}`, 960, 525, 92, { weight: 800, color: '#0b0e14', align: 'center' });
    text(g, 'FULL TIME', 960, 610, 24, { weight: 700, color: 'rgba(232,238,248,0.7)', align: 'center', spacing: 0.3 });
    g.globalAlpha = k(0.55);
    const lw = text(g, `${c.venue.toUpperCase()}  ·  ${c.clipCount} MOMENTS  ·  YOUR GRADE`, 920, 760, 30, { weight: 600, color: 'rgba(232,238,248,0.8)', align: 'center', spacing: 0.12 });
    g.fillStyle = c.letterColor; g.fillRect(920 + lw / 2 + 24, 726, 70, 70);
    text(g, c.letter, 920 + lw / 2 + 59, 765, 60, { weight: 800, italic: true, color: inkFor(c.letterColor), align: 'center' });
  } else {
    g.globalAlpha = k(0.1);
    text(g, 'DIRECTOR RATING', 960, 200, 36, { weight: 700, color: BRAND_2, align: 'center', spacing: 0.4 });
    g.globalAlpha = k(0.2);
    const s = 0.6 + 0.4 * k(0.2);
    g.save(); g.translate(960, 420); g.scale(s, s);
    g.shadowColor = c.letterColor; g.shadowBlur = 60;
    text(g, c.letter, 0, 10, 320, { weight: 800, italic: true, color: c.letterColor, align: 'center' });
    g.restore();
    g.globalAlpha = k(0.45);
    text(g, `${Math.round(c.rating)} / 100  ·  ${c.goalsLine}`, 960, 640, 38, { weight: 700, align: 'center', spacing: 0.08 });
    text(g, `${c.homeName.toUpperCase()} ${c.score.home}–${c.score.away} ${c.awayName.toUpperCase()}`, 960, 700, 30, { weight: 600, color: 'rgba(232,238,248,0.75)', align: 'center', spacing: 0.08 });
    g.globalAlpha = k(0.75);
    text(g, 'DIRECTED BY YOU', 960, 840, 34, { weight: 700, color: 'rgba(232,238,248,0.8)', align: 'center', spacing: 0.35 });
    text(g, 'SPORTS DIRECTOR', 960, 910, 76, { weight: 800, italic: true, align: 'center', spacing: 0.06 });
    g.fillStyle = brandGrad(g, 700, 1220); g.fillRect(700, 960, 520, 6);
  }
  g.restore();
}

/** Paint all overlays for `view` onto `g` (which already holds the program frame). */
export function drawOverlays(g: CanvasRenderingContext2D, W: number, H: number, c: CardInfo, view: OverlayView, time: number): void {
  g.save();
  g.scale(W / 1920, H / 1080);
  if (view.scorebug) drawScorebug(g, c, view.scorebug);
  if (view.replay) drawReplayBug(g, view.replay, time);
  if (view.lowerThird) drawLowerThird(g, view.lowerThird);
  if (view.stamp) drawStamp(g, view.stamp);
  if (view.card) drawCard(g, c, view.card);
  if (view.tag && !view.card && !view.replay) {
    g.globalAlpha = 0.85;
    g.shadowColor = 'rgba(0,0,0,0.7)'; g.shadowBlur = 6;
    text(g, view.tag, 1920 - 96, 82, 22, { weight: 700, color: 'rgba(255,255,255,0.8)', align: 'right', spacing: 0.3 });
    g.globalAlpha = 1; g.shadowColor = 'transparent'; g.shadowBlur = 0;
  }
  drawStinger(g, c, view.stingerAge);
  g.restore();
}
